import { Link, useParams } from 'react-router-dom'
import { VideoPlayer } from '@/components/common/VideoPlayer'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { useGeneration } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Pencil, LayoutTemplate } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

const FRAME_ROLE_LABEL: Record<string, string> = {
  first_frame: '첫 프레임',
  last_frame: '마지막 프레임',
}

export function GenerationDetail() {
  const { id } = useParams()
  const { data: generation, isLoading, error } = useGeneration(id!)

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!generation) return null
  const videoThumbnailUrls = generation.generatedVideo?.thumbnailUrls
    ?? (generation.generatedVideo?.thumbnailUrl ? [generation.generatedVideo.thumbnailUrl] : [])
  const primaryVideoThumbnailUrl = videoThumbnailUrls[0] ?? undefined

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/generations">생성된 비디오</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">생성된 비디오 상세</h1>
        </div>
        <Link to={`/admin/generations/${id}/edit`}>
          <Button>
            <Pencil className="w-4 h-4 mr-2" />
            수정
          </Button>
        </Link>
      </div>

      {/* Soft-deleted banner */}
      {generation.deletedAt && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
            삭제됨
          </span>
          <span className="text-sm text-orange-700">
            사용자가 {new Date(generation.deletedAt).toLocaleString('ko-KR')}에 삭제했습니다
          </span>
        </div>
      )}

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">사용자 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{generation.userId}</div>
            {generation.user?.email && (
              <div className="text-xs text-gray-400 mt-1">{generation.user.email}</div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{generation.ratio}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">해상도</label>
            <div className="mt-1 text-gray-900">{generation.resolution || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">길이</label>
            <div className="mt-1 text-gray-900">{generation.duration ? `${generation.duration}초` : '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">카메라 고정</label>
            <div className="mt-1 text-gray-900">{generation.cameraFixed ? '예' : '아니오'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">모션 스타일</label>
            <div className="mt-1 text-gray-900">
              {generation.motionStyle
                ? generation.motionStyle === 'aggressive'
                  ? '역동적인 움직임'
                  : generation.motionStyle === 'conservative'
                    ? '부드러운 움직임'
                    : generation.motionStyle
                : '기본'}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">오디오 미포함</label>
            <div className="mt-1 text-gray-900">{generation.noAudio ? '예' : '아니오'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Task ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{generation.taskId || '-'}</div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">템플릿</label>
            {generation.template ? (
              <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {generation.template.thumbnailUrl ? (
                  <ImageWithFallback
                    src={generation.template.thumbnailUrl}
                    alt={generation.template.name}
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                    <LayoutTemplate className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <Link
                    to={`/admin/templates/${generation.template.id}`}
                    className="font-medium text-gray-900 hover:text-primary transition-colors"
                  >
                    {generation.template.name}
                  </Link>
                  {generation.template.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{generation.template.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">생성일시</label>
            <div className="mt-1 text-gray-900 text-sm">
              {new Date(generation.createdAt).toLocaleString('ko-KR')}
            </div>
          </div>

          {generation.frameImages && generation.frameImages.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">프레임 이미지</label>
              <div className="mt-1 text-gray-900">{generation.frameImages.length}개</div>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {generation.prompt}
          </div>
        </div>

        {generation.frameImages && generation.frameImages.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-4">
              프레임 이미지 ({generation.frameImages.length}개)
            </label>
            <div className="grid grid-cols-4 gap-4">
              {generation.frameImages.map((frame, idx) => (
                <div key={idx} className="bg-gray-100 rounded-lg overflow-hidden">
                  <ImageWithFallback
                    src={frame.thumbnailUrl}
                    alt={`프레임 이미지 ${idx + 1}`}
                    className="w-full h-auto object-contain"
                    fallbackClassName="h-24"
                    loading="lazy"
                  />
                  <div className="text-xs text-gray-500 text-center py-1">
                    {FRAME_ROLE_LABEL[frame.role] || frame.role}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Generated Video */}
      {generation.generatedVideo && (
        <div className="bg-white rounded-lg border p-6">
          <label className="text-sm font-medium text-gray-700 block mb-4">
            생성된 비디오
          </label>
          <div className="max-w-2xl">
            <VideoPlayer
              src={generation.generatedVideo.videoUrl}
              poster={primaryVideoThumbnailUrl}
              controls
              className="w-full rounded-lg border shadow-lg"
            />
            {generation.generatedVideo.durationSeconds && (
              <div className="text-xs text-gray-500 font-mono text-center mt-2">
                {generation.generatedVideo.durationSeconds}초
              </div>
            )}
            {videoThumbnailUrls.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2">추출된 썸네일 ({videoThumbnailUrls.length}개)</div>
                <div className="grid grid-cols-4 gap-2">
                  {videoThumbnailUrls.map((url, index) => (
                    <ImageWithFallback
                      key={`${url}-${index}`}
                      src={url}
                      alt={`비디오 썸네일 ${index + 1}`}
                      className="w-full object-cover rounded border border-gray-200"
                      style={{ aspectRatio: generation.ratio.replace(':', '/') }}
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage Info */}
      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">사용량 정보</label>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">비용 (USD)</label>
            <div className="mt-1 text-gray-900 font-mono">
              ${((generation.costUsdCents ?? 0) / 100).toFixed(2)}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Task ID</label>
            <div className="mt-1 text-gray-900 font-mono">
              {generation.taskId || '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
